import { useEffect, useRef } from 'react'

import {
  IconAlertTriangle,
  IconFlask,
  IconGift,
  IconRefresh,
} from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useContextStore } from '@/stores/context'
import { useSandboxStore } from '@/stores/sandbox'
import { useSandboxDialogStore } from '@/stores/sandbox-dialog'

import { useAccessState } from '../access/hooks/use-access-state'
import {
  trackFreePlanBannerClicked,
  trackFreePlanBannerViewed,
} from '../free-plan/free-plan-banner.tracking'
import {
  trackNoPlanBannerClicked,
  trackNoPlanBannerViewed,
} from '../no-plan/no-plan-banner.tracking'
import {
  trackReactivateBannerClicked,
  trackReactivateBannerViewed,
} from '../reactivate/reactivate-banner.tracking'
import {
  trackSandboxBannerClicked,
  trackSandboxBannerViewed,
} from '../sandbox/sandbox-banner/sandbox-banner.tracking'

import locales from './locales'

type BannerKind = 'reactivate' | 'free' | 'free-depleted' | 'no-plan'

// Compact mobile counterparts of the sidebar/dashboard banners. The sidebar is
// hidden on mobile, so this top strip surfaces the same access-state prompts.
// Yellow strips use dark text to match the desktop banners.
const BANNER_CLASS: Record<BannerKind, string> = {
  reactivate: 'bg-indigo-600 text-white',
  free: 'bg-blue-600 text-white',
  'free-depleted': 'bg-red-600 text-white',
  'no-plan': 'bg-yellow-400 text-zinc-900',
}

const BANNER_ICON: Record<BannerKind, typeof IconRefresh> = {
  reactivate: IconRefresh,
  free: IconGift,
  'free-depleted': IconGift,
  'no-plan': IconAlertTriangle,
}

export function MobileBannersBar() {
  const { t } = useComponentTranslation(locales)
  const navigate = useNavigate()
  const isSandboxMode = useSandboxStore((s) => s.isSandboxMode)
  const openSandboxDialog = useSandboxDialogStore((s) => s.openDialog)
  const userRole = useContextStore((s) =>
    s.userAccountContext?.userAccountInformation.role === 'Administrator'
      ? 'Administrator'
      : 'User',
  )

  const { accessState, planFeature, isManaged, freePlan } = useAccessState()

  const depleted = accessState === 'free-depleted'
  const freeEnvelopesTotal = freePlan?.envelopesTotal ?? 3
  const freeEnvelopesLeft = freePlan?.envelopesRemaining ?? 0

  const bannerKind: BannerKind | null =
    accessState === 'reactivate'
      ? 'reactivate'
      : accessState === 'free'
        ? 'free'
        : accessState === 'free-depleted'
          ? 'free-depleted'
          : accessState === 'no-plan'
            ? 'no-plan'
            : null

  const isFreeKind = bannerKind === 'free' || bannerKind === 'free-depleted'

  const hasTrackedSandbox = useRef(false)
  const hasTrackedBanner = useRef(false)

  useEffect(() => {
    if (isSandboxMode && !hasTrackedSandbox.current) {
      trackSandboxBannerViewed({
        source: 'mobile-banner',
        userRole,
        currentPlan: planFeature,
        isManaged,
      })
      hasTrackedSandbox.current = true
    }
  }, [isSandboxMode, userRole, planFeature, isManaged])

  useEffect(() => {
    if (!bannerKind || hasTrackedBanner.current) return
    hasTrackedBanner.current = true
    if (bannerKind === 'reactivate') {
      trackReactivateBannerViewed({ source: 'mobile-banner', planFeature })
    } else if (bannerKind === 'free' || bannerKind === 'free-depleted') {
      trackFreePlanBannerViewed({
        source: 'mobile-banner',
        envelopesLeft: freeEnvelopesLeft,
        envelopesTotal: freeEnvelopesTotal,
        depleted,
      })
    } else {
      trackNoPlanBannerViewed({ source: 'mobile-banner', planFeature })
    }
  }, [
    bannerKind,
    planFeature,
    isManaged,
    freeEnvelopesLeft,
    freeEnvelopesTotal,
    depleted,
  ])

  if (!isSandboxMode && !bannerKind) return null

  const handleBannerClick = () => {
    if (bannerKind === 'reactivate') {
      trackReactivateBannerClicked({ source: 'mobile-banner', planFeature })
    } else if (isFreeKind) {
      trackFreePlanBannerClicked({
        source: 'mobile-banner',
        envelopesLeft: freeEnvelopesLeft,
        envelopesTotal: freeEnvelopesTotal,
        depleted,
      })
    } else if (bannerKind === 'no-plan') {
      trackNoPlanBannerClicked({ source: 'mobile-banner', planFeature })
    }
    void navigate({ to: '/settings/billing/plans' })
  }

  const bannerLabel =
    bannerKind === 'reactivate'
      ? t('reactivate')
      : bannerKind === 'free'
        ? t('free', { count: freeEnvelopesLeft })
        : bannerKind === 'free-depleted'
          ? t('freeDepleted')
          : t('noPlan')

  const BannerIcon = bannerKind ? BANNER_ICON[bannerKind] : IconAlertTriangle

  return (
    <div className="top-0 z-10 flex flex-col md:hidden">
      {isSandboxMode && (
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 bg-orange-600 px-3 py-2 text-xs font-medium text-white dark:bg-orange-700"
          onClick={() => {
            trackSandboxBannerClicked({
              source: 'mobile-banner',
              userRole,
              currentPlan: planFeature,
              isManaged,
            })
            openSandboxDialog('mobile-banner')
          }}
        >
          <IconFlask className="h-3.5 w-3.5" />
          {t('sandbox')}
        </button>
      )}
      {bannerKind && (
        <button
          type="button"
          className={`flex w-full items-center justify-center gap-2 px-3 py-2 text-xs font-medium ${BANNER_CLASS[bannerKind]}`}
          onClick={handleBannerClick}
        >
          <BannerIcon className="h-3.5 w-3.5" />
          {bannerLabel}
        </button>
      )}
    </div>
  )
}
